import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Zap, Mail, ArrowUpRight, ExternalLink, Star, Play, Clock, ChevronRight, ChevronDown, Folder, FolderOpen, FileCode2, Sparkles, WalletCards, Menu, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import LightningBackdrop from "./components/LightningBackdrop";
import CodeBlock from "./components/CodeBlock";
import { GAMES, PAYMENT_AND_TERMS, SHOWCASE_VIDEOS, SKILLS, SNIPPETS } from "./data";

const ROBLOX_STATS_URL =
  "https://portfolio-roblox-stats.thunderthighba.workers.dev/api/roblox/games";
const OWNER_TIME_ZONE = "America/New_York";

const NAV = [
  { id: "top", label: "Home" },
  { id: "games", label: "Games" },
  { id: "showcase", label: "Systems" },
  { id: "skills", label: "Skills" },
  { id: "process", label: "Process" },
  { id: "contact", label: "Contact" },
];

function formatCompactNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }

  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function getTimeZoneOffsetMinutes(timeZone, date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value])
  );

  const utcTime = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return Math.round((utcTime - date.getTime()) / 60000);
}

function formatTimeDifference(ownerOffset, viewerOffset) {
  const diffHours = (ownerOffset - viewerOffset) / 60;

  if (diffHours === 0) {
    return "same time as you";
  }

  const absoluteHours = Math.abs(diffHours);
  const formattedHours = Number.isInteger(absoluteHours)
    ? absoluteHours
    : absoluteHours.toFixed(1);
  return `${formattedHours} hour${absoluteHours === 1 ? "" : "s"} ${diffHours > 0 ? "ahead of you" : "behind you"}`;
}

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header
      className="fixed left-0 top-0 z-50 w-full border-b border-white/10 bg-[#050507]/86 backdrop-blur-xl"
      data-testid="main-nav"
    >
      <nav
        className="mx-auto flex min-h-[76px] max-w-7xl items-center justify-between gap-5 px-6 sm:px-8"
        aria-label="Primary"
      >
        <a
          href="#top"
          onClick={closeMenu}
          data-testid="nav-logo"
          className="group flex min-w-0 items-center gap-3"
        >
          <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-cyan-300/30 bg-cyan-300/10 text-cyan-100 shadow-[0_0_26px_rgba(0,240,255,0.18)] transition-all duration-300 group-hover:border-lime-300/50 group-hover:bg-lime-300 group-hover:text-black">
            <Zap size={20} strokeWidth={2.5} />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-outfit text-lg font-extrabold leading-tight text-white">
              Thunder_thigh7
            </span>
          </span>
        </a>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              data-testid={`nav-${item.id}`}
              className="rounded-full border border-transparent px-4 py-2 font-manrope text-xs font-semibold uppercase tracking-[0.12em] text-zinc-300 transition-all duration-300 hover:border-cyan-300/70 hover:bg-transparent hover:text-lime-200 hover:shadow-[0_0_18px_rgba(0,240,255,0.34),inset_0_0_14px_rgba(163,230,53,0.08)]"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="https://discord.com/channels/@me"
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-cyan-300/35 bg-cyan-300/10 px-5 font-manrope text-xs font-bold uppercase tracking-[0.14em] text-cyan-100 transition-all duration-300 hover:bg-cyan-300 hover:text-black"
          >
            Discord
            <ExternalLink size={14} strokeWidth={2.4} />
          </a>
          <a
            href="https://www.roblox.com/users/2776451302/profile"
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-lime-300/35 bg-lime-300/10 px-5 font-manrope text-xs font-bold uppercase tracking-[0.14em] text-lime-200 transition-all duration-300 hover:bg-lime-300 hover:text-black"
          >
            Roblox
            <ExternalLink size={14} strokeWidth={2.4} />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-cyan-100 transition-all duration-300 hover:border-cyan-300/35 hover:bg-cyan-300/10 lg:hidden"
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-nav"
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {isMenuOpen && (
        <div
          id="mobile-nav"
          className="border-t border-white/10 bg-[#050507]/95 px-6 py-4 shadow-[0_20px_48px_rgba(0,0,0,0.42)] lg:hidden"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-2">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={closeMenu}
                data-testid={`mobile-nav-${item.id}`}
                className="rounded-lg px-3 py-3 font-manrope text-sm font-semibold uppercase tracking-[0.12em] text-zinc-200 transition-all hover:bg-cyan-300/10 hover:text-cyan-100"
              >
                {item.label}
              </a>
            ))}
            <a
              href="https://discord.com/channels/@me"
              onClick={closeMenu}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-cyan-300/35 bg-cyan-300/10 px-5 font-manrope text-xs font-bold uppercase tracking-[0.14em] text-cyan-100 transition-all hover:bg-cyan-300 hover:text-black"
            >
              Discord
              <ExternalLink size={14} strokeWidth={2.4} />
            </a>
            <a
              href="https://www.roblox.com/users/2776451302/profile"
              onClick={closeMenu}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-lime-300/35 bg-lime-300/10 px-5 font-manrope text-xs font-bold uppercase tracking-[0.14em] text-lime-200 transition-all hover:bg-lime-300 hover:text-black"
            >
              Roblox Profile
              <ExternalLink size={14} strokeWidth={2.4} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function CustomCursor() {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const targetPositionRef = useRef({ x: 0, y: 0 });
  const ringPositionRef = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);
  const [isFinePointer, setIsFinePointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: fine)");
    const updatePointerMode = () => {
      setIsFinePointer(mediaQuery.matches);
      document.body.classList.toggle("custom-cursor-enabled", mediaQuery.matches);
    };

    updatePointerMode();
    mediaQuery.addEventListener?.("change", updatePointerMode);

    const moveCursor = (event) => {
      targetPositionRef.current = { x: event.clientX, y: event.clientY };
      dotRef.current?.style.setProperty("transform", `translate3d(${event.clientX}px, ${event.clientY}px, 0)`);
      setIsVisible(true);
      setIsInteractive(Boolean(event.target.closest("a, button, input, textarea, select, video, [role='button']")));
    };

    const hideCursor = () => {
      setIsVisible(false);
      setIsInteractive(false);
    };
    const animateRing = () => {
      const target = targetPositionRef.current;
      const ring = ringPositionRef.current;

      ring.x += (target.x - ring.x) * 0.18;
      ring.y += (target.y - ring.y) * 0.18;

      ringRef.current?.style.setProperty("transform", `translate3d(${ring.x}px, ${ring.y}px, 0)`);
      animationRef.current = window.requestAnimationFrame(animateRing);
    };

    animationRef.current = window.requestAnimationFrame(animateRing);
    window.addEventListener("pointermove", moveCursor, { passive: true });
    window.addEventListener("pointerleave", hideCursor);
    window.addEventListener("blur", hideCursor);

    return () => {
      mediaQuery.removeEventListener?.("change", updatePointerMode);
      window.removeEventListener("pointermove", moveCursor);
      window.removeEventListener("pointerleave", hideCursor);
      window.removeEventListener("blur", hideCursor);
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
      }
      document.body.classList.remove("custom-cursor-enabled");
    };
  }, []);

  if (!isFinePointer) return null;

  return (
    <>
      <span
        ref={ringRef}
        className={`custom-cursor-ring ${isInteractive ? "is-interactive" : ""} ${isVisible ? "opacity-100" : "opacity-0"}`}
      />
      <span
        ref={dotRef}
        className={`custom-cursor-dot ${isInteractive ? "is-interactive" : ""} ${isVisible ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
}

function Hero() {
  return (
    <section
      id="top"
      className="relative isolate overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-20"
      data-testid="hero-section"
    >
      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
        <div className="max-w-6xl">
            <div className="mb-6 flex w-fit rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
              // 00 - Intro
            </div>

            <h1 className="w-fit max-w-full pb-5 bg-gradient-to-r from-white via-cyan-200 to-lime-200 bg-clip-text font-outfit text-6xl font-black leading-[1.12] text-transparent drop-shadow-[0_0_34px_rgba(0,240,255,0.36)] sm:text-8xl lg:text-9xl">
              I code entire Roblox games
            </h1>

            <p className="mt-6 max-w-2xl font-manrope text-lg leading-relaxed text-zinc-200 sm:text-xl">
              I&apos;m a full stack Roblox programmer who&apos;s been scripting every
              day for years. I code{" "}
              <span className="neon-cyan font-semibold text-cyan-300">
                entire games
              </span>
              , from frontend to backend.
            </p>

        </div>
      </div>
    </section>
  );
}

function GameCard({ game }) {
  const isFullyCoded = game.contribution === "Fully coded";
  const contributionLabel = isFullyCoded ? "Fully coded by me." : "Partially coded by me.";

  return (
    <article
      data-testid={`game-card-${game.id}`}
      className="group relative isolate flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d14] transition-[transform,border-color,box-shadow] duration-500 [backface-visibility:hidden] [transform:translate3d(0,0,0)] [will-change:transform] hover:[transform:translate3d(0,-4px,0)] hover:border-cyan-400/40 hover:shadow-[0_0_40px_-10px_rgba(0,240,255,0.4)]"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full flex-none overflow-hidden bg-[#0d0d14] [backface-visibility:hidden] [transform:translateZ(0)]">
        <img
          src={game.image}
          alt={game.name}
          className="absolute inset-0 block h-full w-full object-cover [backface-visibility:hidden]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d14] via-[#0d0d14]/30 to-transparent" />
        <div className="absolute inset-x-0 -bottom-px h-8 bg-gradient-to-t from-[#0d0d14] via-[#0d0d14]/95 to-transparent" />
        <div className={`absolute left-3 top-3 inline-flex items-center rounded-full border bg-black/75 px-3 py-1.5 font-manrope text-[10px] font-bold uppercase tracking-[0.14em] backdrop-blur ${
          isFullyCoded
            ? "border-lime-400/50 text-lime-200"
            : "border-cyan-400/50 text-cyan-200"
        }`}>
          {game.contribution}
        </div>
        <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/35 bg-black/90 px-3.5 py-2 font-manrope text-xs font-bold uppercase tracking-[0.06em] text-cyan-100">
            <Play size={12} fill="currentColor" />
            {game.plays} visits
          </div>
          {game.peakCcu && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/90 px-3.5 py-2 font-manrope text-xs font-bold uppercase tracking-[0.06em] text-zinc-100">
              <Star size={12} />
              {game.peakCcu} peak CCU
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="relative z-10 -mt-px flex flex-1 flex-col bg-[#0d0d14] p-5 pt-4">
        <h3 className="font-outfit text-xl font-bold text-white">
          {game.name}
        </h3>
        {game.contribution && (
          <div className="mt-3">
            {game.contribution && (
              <div className={`border-l-2 pl-3 font-manrope text-base font-bold leading-snug ${
                isFullyCoded
                  ? "border-lime-400 text-lime-200"
                  : "border-cyan-400 text-cyan-100"
              }`}>
                <span className="mr-2 text-lg leading-none">✓</span>
                {contributionLabel}
              </div>
            )}
          </div>
        )}
        {game.note && (
          <p className="mt-3 font-manrope text-sm leading-relaxed text-zinc-300">
            {game.note}
          </p>
        )}
        {game.href && (
          <div className="mt-auto pt-5">
            <a
              href={game.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-cyan-400/50 bg-cyan-400/10 px-4 py-2 font-manrope text-xs font-semibold tracking-[0.12em] text-cyan-100 transition-all hover:bg-cyan-400 hover:text-black"
            >
              View on Roblox
              <ExternalLink size={13} strokeWidth={2.5} />
            </a>
          </div>
        )}
      </div>
    </article>
  );
}

function GamesSection({ games }) {
  return (
    <section id="games" className="relative py-20 sm:py-28" data-testid="games-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
              // 01 - Games
            </div>
            <h2 className="mt-4 max-w-3xl font-outfit text-4xl font-extrabold leading-[0.95] tracking-tight text-white drop-shadow-[0_0_22px_rgba(0,240,255,0.16)] sm:text-5xl lg:text-6xl">
              <span className="text-cyan-300">Games</span>{" "}
              <span className="text-white">I&apos;ve coded</span>{" "}
              <span className="text-lime-300">for</span>
            </h2>
          </div>
          <p className="max-w-md font-manrope text-sm leading-relaxed text-zinc-200 sm:text-base">
            Roblox games with my exact coding contribution shown on
            each card.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {games.map((g) => (
            <GameCard key={g.id} game={g} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcaseSection() {
  return (
    <section id="showcase" className="relative py-20 sm:py-28" data-testid="showcase-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
              // 02 - Systems
            </div>
            <h2 className="mt-4 max-w-3xl font-outfit text-4xl font-extrabold leading-[0.95] tracking-tight text-white drop-shadow-[0_0_22px_rgba(0,240,255,0.16)] sm:text-5xl lg:text-6xl">
              <span className="text-lime-300">Systems</span>{" "}
              <span className="text-white">I&apos;ve</span>{" "}
              <span className="text-cyan-300">coded</span>
            </h2>
          </div>
          <p className="max-w-md font-manrope text-sm leading-relaxed text-zinc-200 sm:text-base">
            A small sample of the Roblox systems and mechanics I can script.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {SHOWCASE_VIDEOS.map((video) => (
            <article
              key={video.id}
              className="group isolate overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d14] transition-all duration-300 [transform:translateZ(0)] hover:-translate-y-1 hover:border-cyan-400/40 hover:shadow-[0_0_40px_-10px_rgba(0,240,255,0.4)]"
            >
              <div className="relative aspect-video overflow-hidden bg-black [backface-visibility:hidden]">
                {video.src ? (
                  <video
                    className="block h-full w-full object-cover [backface-visibility:hidden]"
                    src={video.src}
                    poster={video.poster}
                    controls
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-4 border-b border-white/5 bg-[radial-gradient(circle_at_center,rgba(0,240,255,0.14),rgba(13,13,20,0.28)_42%,rgba(0,0,0,0.72))] px-6 text-center">
                    <div className="grid h-14 w-14 place-items-center rounded-full border border-cyan-400/45 bg-cyan-400/10 text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.24)]">
                      <Play size={22} fill="currentColor" strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="font-manrope text-[10px] font-semibold uppercase tracking-[0.22em] text-cyan-200">
                        Video Slot
                      </div>
                      <div className="mt-1 font-manrope text-sm text-zinc-300">
                        Add a clip path in data.js
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative z-10 -mt-px bg-[#0d0d14] p-5">
                <div className="font-manrope text-[10px] font-semibold uppercase tracking-[0.22em] text-lime-300">
                  {video.label}
                </div>
                <h3 className="mt-2 font-outfit text-xl font-bold text-white">
                  {video.title}
                </h3>
                <p className="mt-2 font-manrope text-sm leading-relaxed text-zinc-200">
                  {video.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const STUDIO_TREE = [
  {
    id: "server-script-service",
    name: "ServerScriptService",
    kind: "folder",
    children: [
      {
        id: "server",
        name: "Server",
        kind: "folder",
        children: [
          {
            id: "core",
            name: "Core",
            kind: "folder",
            children: [
              {
                id: "server-bootstrap",
                name: "ServerBootstrap.server.lua",
                kind: "script",
              },
              {
                id: "server-loader",
                name: "ServerLoader.lua",
                kind: "module",
              },
              {
                id: "server-loader-config",
                name: "ServerLoaderConfig.lua",
                kind: "module",
              },
            ],
          },
          {
            id: "services",
            name: "Services",
            kind: "folder",
            children: [
              {
                id: "game",
                name: "Game",
                kind: "folder",
                children: [
                  {
                    id: "round-service",
                    name: "RoundService",
                    kind: "folder",
                    children: [
                      { id: "round-api", name: "API.lua", kind: "module" },
                      {
                        id: "round-config",
                        name: "Config",
                        kind: "folder",
                        children: [
                          { id: "round-settings", name: "Settings.lua", kind: "module" },
                        ],
                      },
                      {
                        id: "round-core",
                        name: "Core",
                        kind: "folder",
                        children: [
                          { id: "round-methods", name: "Methods.lua", kind: "module" },
                        ],
                      },
                    ],
                  },
                  {
                    id: "combat-service",
                    name: "CombatService",
                    kind: "folder",
                    children: [
                      { id: "combat-api", name: "API.lua", kind: "module" },
                      {
                        id: "combat-config",
                        name: "Config",
                        kind: "folder",
                        children: [
                          { id: "combat-settings", name: "Settings.lua", kind: "module" },
                        ],
                      },
                      {
                        id: "combat-core",
                        name: "Core",
                        kind: "folder",
                        children: [
                          { id: "combat-methods", name: "Methods.lua", kind: "module" },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                id: "framework",
                name: "Framework",
                kind: "folder",
                children: [
                  {
                    id: "data-service",
                    name: "DataService",
                    kind: "folder",
                    children: [
                      {
                        id: "data-config",
                        name: "Config",
                        kind: "folder",
                      },
                      {
                        id: "data-structures",
                        name: "DataStructures",
                        kind: "folder",
                      },
                      {
                        id: "data-validation",
                        name: "Validation",
                        kind: "folder",
                      },
                      {
                        id: "data-read-write",
                        name: "Read / Write",
                        kind: "folder",
                      },
                      {
                        id: "profile-store",
                        name: "ProfileStore",
                        kind: "folder",
                      },
                    ],
                  },
                  {
                    id: "networking-service",
                    name: "NetworkingService",
                    kind: "folder",
                    children: [
                      {
                        id: "networking-core",
                        name: "Core",
                        kind: "folder",
                      },
                      {
                        id: "networking-backends",
                        name: "Backends",
                        kind: "folder",
                      },
                      {
                        id: "networking-registry",
                        name: "Registry",
                        kind: "folder",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

function getVisibleStudioRows(tree, openIds) {
  const rows = [];
  const stack = tree
    .slice()
    .reverse()
    .map((node) => ({ node, level: 0 }));

  while (stack.length) {
    const row = stack.pop();
    rows.push(row);

    if (row.node.children?.length && openIds.has(row.node.id)) {
      row.node.children
        .slice()
        .reverse()
        .forEach((child) => {
          stack.push({ node: child, level: row.level + 1 });
        });
    }
  }

  return rows;
}

function StudioTreeRow({ node, level, openIds, hoveredId, onHover, onToggle }) {
  const hasChildren = Boolean(node.children?.length);
  const isOpen = openIds.has(node.id);
  const isHovered = hoveredId === node.id;
  const isService = node.kind === "service";
  const FileIcon = node.kind === "script" || node.kind === "module" ? FileCode2 : isOpen ? FolderOpen : Folder;
  const handleHover = () => onHover(node.id);

  return (
    <div>
      <button
        type="button"
        onFocus={handleHover}
        onMouseEnter={handleHover}
        onClick={() => hasChildren && onToggle(node.id)}
        className={`studio-tree-row group flex h-10 w-full items-center gap-3 rounded-lg pr-3 text-left transition-all duration-200 ${isHovered ? "is-active" : ""} ${hasChildren ? "hover:bg-cyan-300/[0.08]" : "cursor-default"}`}
        style={{ paddingLeft: `${level * 22 + 12}px` }}
        aria-expanded={hasChildren ? isOpen : undefined}
      >
        <span className="grid h-5 w-5 place-items-center text-zinc-500">
          {hasChildren ? (
            isOpen ? <ChevronDown size={17} /> : <ChevronRight size={17} />
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-zinc-700" />
          )}
        </span>
        <FileIcon
          size={18}
          className={`${node.kind === "script" || node.kind === "module" ? "text-cyan-300" : isService ? "text-lime-300" : "text-yellow-300"} transition-colors group-hover:text-white`}
        />
        <span className={`truncate font-manrope text-sm font-semibold ${isService ? "text-lime-100" : "text-zinc-200"}`}>
          {node.name}
        </span>
      </button>
    </div>
  );
}

function StudioStructureShowcase() {
  const [openIds, setOpenIds] = useState(
    () => new Set(["server-script-service", "server", "core", "services", "game", "round-service", "round-config", "round-core", "framework", "data-service", "networking-service"])
  );
  const [hoveredId, setHoveredId] = useState("server-script-service");
  const hoverAudioRef = useRef(null);
  const lastHoverSoundRef = useRef(0);
  const visibleRows = getVisibleStudioRows(STUDIO_TREE, openIds);

  const playHoverSound = () => {
    const now = performance.now();
    if (now - lastHoverSoundRef.current < 42) return;
    lastHoverSoundRef.current = now;

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const context = hoverAudioRef.current || new AudioContext();
    hoverAudioRef.current = context;

    if (context.state === "suspended") {
      context.resume().catch(() => {});
    }

    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const filter = context.createBiquadFilter();

    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(820, start);
    oscillator.frequency.exponentialRampToValueAtTime(1180, start + 0.045);

    filter.type = "highpass";
    filter.frequency.value = 420;

    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.022, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.065);

    oscillator.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + 0.075);
  };

  const handleRowHover = (id) => {
    if (id !== hoveredId) {
      setHoveredId(id);
      playHoverSound();
    }
  };

  useEffect(() => {
    return () => {
      hoverAudioRef.current?.close?.();
    };
  }, []);

  const toggleNode = (id) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="skills-proof-panel relative overflow-hidden rounded-2xl border border-white/10 bg-[#080910]/72 p-4 shadow-[0_0_44px_-24px_rgba(0,240,255,0.62)] backdrop-blur-xl sm:p-5">
      <div className="relative z-10">
        <div className="mb-4 flex items-center justify-between rounded-xl border border-cyan-300/20 bg-cyan-300/[0.08] px-4 py-3">
          <div className="flex items-center gap-3 font-manrope text-sm font-bold uppercase tracking-[0.12em] text-cyan-100 drop-shadow-[0_0_10px_rgba(0,240,255,0.45)] sm:text-base">
            <Sparkles size={18} className="text-cyan-200" />
            Real Studio Structure
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#07080d]/92 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <div className="flex items-center justify-between border-b border-white/5 bg-black/40 px-5 py-4">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
              <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
              <span className="h-3 w-3 rounded-full bg-[#28c840]" />
            </div>
            <div className="font-manrope text-xs uppercase tracking-[0.2em] text-zinc-400">
              Explorer
            </div>
          </div>
          <div className="max-h-[430px] min-h-[380px] overflow-y-auto px-3 py-4">
            {visibleRows.map(({ node, level }) => (
              <StudioTreeRow
                key={node.id}
                node={node}
                level={level}
                openIds={openIds}
                hoveredId={hoveredId}
                onHover={handleRowHover}
                onToggle={toggleNode}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SkillsSection() {
  return (
    <section id="skills" className="relative overflow-hidden py-20 sm:py-28" data-testid="skills-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
              // 03 - Skills
            </div>
            <h2 className="mt-3 font-outfit text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Ideas I can{" "}
              <span className="neon-lime text-lime-400">script</span> for you
            </h2>
            <p className="mt-5 font-manrope text-base leading-relaxed text-zinc-200">
             Here are some of the game systems I can script, along with many others.
            </p>
            <ul className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {SKILLS.map((skill) => (
                <li
                  key={skill.name}
                  className="flex items-center gap-4 font-manrope text-lg font-semibold text-zinc-100 sm:text-xl"
                >
                  <span className="h-3 w-3 flex-none rounded-full bg-cyan-300 shadow-[0_0_16px_rgba(0,240,255,0.9)]" />
                  {skill.name}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <StudioStructureShowcase />
          </div>
        </div>
      </div>
    </section>
  );
}

const PROCESS_STEPS = [
  {
    number: "1",
    title: "Plan",
    description: "We talk through the idea, scope, references, budget, and what the final game should include.",
  },
  {
    number: "2",
    title: "Script",
    description: "I script the systems and keep you updated as the game comes together.",
  },
  {
    number: "3",
    title: "Test",
    description: "We play through it, find issues, adjust details, and make sure it feels right.",
  },
  {
    number: "4",
    title: "Launch",
    description: "I help get it ready to release and will stay for updates.",
  },
];

function ProcessSection() {
  return (
    <section id="process" className="relative overflow-hidden py-16 sm:py-20" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <div className="max-w-5xl">
          <div className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
            // 04 - Process
          </div>
          <h2 className="mt-4 font-outfit text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            From idea to launch
          </h2>
          <p className="mt-5 font-manrope text-base leading-relaxed text-zinc-200">
            A clear path from first message to a playable Roblox game.
          </p>

          <div className="relative mt-12 pl-10">
            <div className="absolute bottom-0 left-3 top-2 w-px bg-lime-300/20" />
            <div className="space-y-10 sm:space-y-12">
              {PROCESS_STEPS.map((step) => (
                <article key={step.title} className="relative">
                  <span className="absolute -left-10 top-1 flex h-6 w-6 items-center justify-center rounded-full border border-lime-300/35 bg-[#07100b] shadow-[0_0_22px_rgba(74,222,128,0.35)]">
                    <span className="h-3 w-3 rounded-full bg-lime-400 shadow-[0_0_16px_rgba(74,222,128,0.95)]" />
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h3 className="font-outfit text-xl font-bold text-white sm:text-2xl">
                      {step.title}
                    </h3>
                    <div className="font-manrope text-sm font-semibold uppercase tracking-[0.18em] text-lime-300">
                      Step {step.number}
                    </div>
                  </div>
                  <p className="mt-3 max-w-3xl font-manrope text-base leading-relaxed text-zinc-300 sm:text-lg">
                    {step.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CodeSection() {
  return (
    <section id="code" className="relative py-16 sm:py-20" data-testid="code-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <div className="max-w-3xl">
          <div className="font-manrope text-xs uppercase tracking-[0.25em] text-lime-400">
            // 03 — Code, not promises
          </div>
          <h2 className="mt-3 font-outfit text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Real snippets from real games.
          </h2>
          <p className="mt-5 font-manrope text-base leading-relaxed text-zinc-200">
            Five things I get asked to build the most — datastores, remotes,
            combat hitboxes, OOP classes, animated UI. Tap a tab, read the
            code, copy it if you want.
          </p>
        </div>

        <Tabs defaultValue={SNIPPETS[0].id} className="mt-12" data-testid="code-tabs">
          <TabsList
            className="flex h-auto w-full flex-wrap items-center justify-start gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-2"
            data-testid="code-tabs-list"
          >
            {SNIPPETS.map((s) => (
              <TabsTrigger
                key={s.id}
                value={s.id}
                data-testid={`code-tab-${s.id}`}
                className="rounded-lg px-4 py-2 font-manrope text-xs uppercase tracking-widest text-zinc-400 transition-all data-[state=active]:bg-cyan-400 data-[state=active]:text-black data-[state=active]:shadow-[0_0_24px_rgba(0,240,255,0.5)]"
              >
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {SNIPPETS.map((s) => (
            <TabsContent key={s.id} value={s.id} className="mt-6">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                <div className="lg:col-span-4">
                  <div className="font-manrope text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                    {s.label}
                  </div>
                  <h3 className="mt-2 font-outfit text-xl font-bold text-white sm:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-3 font-manrope text-sm leading-relaxed text-zinc-200">
                    {s.description}
                  </p>
                </div>
                <div className="lg:col-span-8">
                  <CodeBlock code={s.code} fileName={`${s.id}.lua`} />
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}

function PaymentAndTermsSection() {
  return (
    <section
      id="payment"
      className="relative py-16 sm:py-20"
      data-testid="payment-and-terms-section"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <div className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
          // 04 - Payment &amp; Terms
        </div>
        <h2 className="mt-4 font-outfit text-4xl font-extrabold leading-[0.95] tracking-tight text-white drop-shadow-[0_0_22px_rgba(0,240,255,0.16)] sm:text-5xl lg:text-6xl">
          Conditions
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <article className="h-full rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.06] p-7">
            <div className="flex min-h-8 items-center gap-3 text-cyan-200">
              <WalletCards size={20} />
              <h2 className="font-outfit text-2xl font-bold text-white">Payment methods</h2>
            </div>
            <ul className="mt-5 space-y-1">
              {PAYMENT_AND_TERMS.paymentMethods.map((method) => (
                <li
                  key={method.name}
                  className="flex items-center gap-3 px-4 py-1.5 font-manrope text-base font-semibold text-zinc-100"
                >
                  <span className="h-2 w-2 flex-none rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(0,240,255,0.85)]" />
                  {method.name}
                </li>
              ))}
            </ul>
          </article>

          <article className="h-full rounded-2xl border border-lime-400/25 bg-lime-400/[0.05] p-7">
            <div className="flex min-h-8 items-center">
              <h2 className="font-outfit text-2xl font-bold text-white">🤝 Friendly terms</h2>
            </div>
            <ul className="mt-5 space-y-1">
              {PAYMENT_AND_TERMS.terms.map((term) => (
                <li key={term} className="flex gap-3 px-4 py-1.5 font-manrope text-base leading-relaxed text-zinc-200">
                  <span className="mt-2 h-2 w-2 flex-none rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(0,240,255,0.85)]" />
                  {term}
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const channels = [
    {
      id: "discord",
      label: "Discord",
      value: "thunder_thigh7",
      icon: ExternalLink,
      href: "https://discord.com/channels/@me",
      accent: "text-cyan-300",
    },
    {
      id: "roblox",
      label: "Roblox",
      value: "@Thunder_thigh7",
      icon: ExternalLink,
      href: "https://www.roblox.com/users/2776451302/profile",
      accent: "text-cyan-300",
    },
  ];

  const ownerTime = new Intl.DateTimeFormat("en-US", {
    timeZone: OWNER_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(now);
  const ownerOffset = getTimeZoneOffsetMinutes(OWNER_TIME_ZONE, now);
  const viewerOffset = -now.getTimezoneOffset();
  const timeDifference = formatTimeDifference(ownerOffset, viewerOffset);

  return (
    <section
      id="contact"
      className="relative overflow-hidden py-20 sm:py-28"
      data-testid="contact-section"
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-[140px]"
          style={{ background: "radial-gradient(circle, #00f0ff 0%, transparent 60%)" }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
          <div className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200 shadow-[0_0_24px_rgba(0,240,255,0.12)]">
              // 05 - Contact
          </div>
        <h2 className="mt-3 max-w-4xl font-outfit text-5xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
          Got a project?<br />
          <span className="neon-cyan text-cyan-300">Let&apos;s talk</span>
        </h2>
        <p className="mt-6 max-w-xl font-manrope text-base leading-relaxed text-zinc-200 sm:text-lg">
          If you’ve got a project in mind, just reach out and I’ll see what I can do for you — whether that’s scripting a game from scratch, improving existing code, or building specific systems. I usually respond within 12 hours.
        </p>
        <div className="mt-6 grid max-w-5xl grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex min-h-[92px] w-full flex-col justify-center gap-3 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.07] px-5 py-5 text-cyan-100">
            <div className="flex items-center gap-2 font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
              <Clock size={16} className="text-cyan-300" />
              My time
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-manrope text-base font-semibold leading-relaxed text-white sm:text-lg">
              <span>{ownerTime}</span>
              <span className="text-zinc-200">
                ({timeDifference})
              </span>
            </div>
          </div>
          <div className="flex min-h-[92px] w-full flex-col justify-center gap-3 rounded-xl border border-lime-400/30 bg-lime-400/[0.08] px-5 py-5 text-lime-100 shadow-[0_0_24px_rgba(163,230,53,0.08)]">
            <div className="font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-lime-300">
              Response hours
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-manrope text-base font-semibold leading-relaxed text-white sm:text-lg">
              <span>Mon-Fri: 4 PM - 10 PM</span>
              <span>Sat-Sun: 1 PM - 10 PM</span>
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {channels.map((c) => (
            <a
              key={c.id}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel={c.href.startsWith("http") ? "noreferrer" : undefined}
              data-testid={`contact-${c.id}`}
              className="group relative flex items-center justify-between overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-6 transition-all duration-300 hover:border-cyan-400/40 hover:bg-white/[0.05]"
            >
              <div className="flex items-center gap-5">
                <div className={`grid h-12 w-12 place-items-center rounded-xl border border-white/10 bg-black/40 ${c.accent}`}>
                  <c.icon size={20} />
                </div>
                <div>
                  <div className="font-manrope text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-100">
                    {c.label}
                  </div>
                  <div className="mt-1 font-outfit text-lg font-semibold text-white sm:text-xl">
                    {c.value}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ArrowUpRight
                  size={22}
                  className="text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cyan-300"
                />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 bg-[#050507]/70 px-6 py-8 text-center text-sm text-zinc-500 backdrop-blur-md" data-testid="footer">
     
    </footer>
  );
}

function PortfolioIntro({ isLeaving, onEnter }) {
  return (
    <section
      className={`intro-screen fixed inset-0 z-[80] flex min-h-screen items-center justify-center overflow-hidden bg-[#030307] px-6 text-center transition-all duration-700 ease-out ${isLeaving ? "pointer-events-none opacity-0 scale-[1.025] blur-sm" : "opacity-100 scale-100 blur-0"}`}
      data-testid="portfolio-intro"
    >
      <div className="intro-vignette" />

      <div className="absolute left-5 top-5 hidden font-manrope text-[10px] font-semibold uppercase tracking-[0.28em] text-cyan-100/45 sm:block">
        THUNDER_THIGH7 // LUAU
      </div>

      <div className="relative z-10 flex w-full max-w-3xl flex-col items-center">
        <div className="intro-mark relative grid h-16 w-16 place-items-center rounded-2xl border border-cyan-300/45 bg-cyan-300/15 text-cyan-100 shadow-[0_0_42px_rgba(0,240,255,0.34)]">
          <Zap size={30} strokeWidth={2.5} />
        </div>

        <div className="mt-10 font-outfit text-lg font-bold uppercase tracking-[0.36em] text-lime-200 drop-shadow-[0_0_14px_rgba(163,230,53,0.65)]">
          Portfolio
        </div>
        <h1 className="mt-4 font-outfit text-5xl font-extrabold leading-none tracking-tight text-white sm:text-6xl lg:text-7xl">
          Thunder_thigh7
        </h1>
        <p className="mt-4 font-outfit text-2xl font-bold text-cyan-200 sm:text-3xl">
          Roblox Scripter
        </p>

        <button
          type="button"
          onClick={onEnter}
          className="group mt-12 inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full border border-cyan-300/50 bg-cyan-300/10 px-9 font-manrope text-xs font-bold uppercase tracking-[0.28em] text-cyan-50 shadow-[0_0_34px_rgba(0,240,255,0.16)] transition-all duration-300 hover:border-lime-300/70 hover:bg-lime-300 hover:text-black hover:shadow-[0_0_42px_rgba(163,230,53,0.28)] focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:ring-offset-2 focus:ring-offset-[#030307]"
          data-testid="enter-portfolio"
        >
          <Zap size={15} strokeWidth={2.6} />
          Enter Portfolio
          <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </section>
  );
}

function App() {
  const [robloxStats, setRobloxStats] = useState({});

  // Smooth-scroll anchor offset for fixed nav
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const gameIdsByUniverse = new Map(
      GAMES.filter((game) => game.universeId).map((game) => [game.universeId, game.id])
    );

    async function loadRobloxStats() {
      try {
        const response = await fetch(ROBLOX_STATS_URL);
        if (!response.ok) {
          throw new Error(`Failed to fetch Roblox stats: ${response.status}`);
        }

        const payload = await response.json();
        const nextStats = {};

        payload.games?.forEach((details) => {
          const gameId = gameIdsByUniverse.get(details.id);
          if (gameId) {
            nextStats[gameId] = details;
          }
        });

        if (!cancelled) {
          setRobloxStats(nextStats);
        }
      } catch (error) {
        console.warn(error);
      }
    }

    loadRobloxStats();
    const intervalId = window.setInterval(loadRobloxStats, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const games = GAMES.map((game) => {
    const liveDetails = robloxStats[game.id];
    const visits = liveDetails?.visits;

    return {
      ...game,
      plays: visits !== undefined ? formatCompactNumber(visits) : game.plays,
    };
  });

  return (
    <div className="App relative min-h-screen bg-[#050507] font-manrope text-white">
      <div className="transition-opacity duration-500 ease-out opacity-100">
        <div className="fixed inset-0 z-0 pointer-events-none">
          <LightningBackdrop />
        </div>
        <Header />
        <div className="pt-[76px]">
          <main className="relative z-10">
            <Hero />
            <GamesSection games={games} />
            <ShowcaseSection />
            <SkillsSection />
            <ProcessSection />
            <ContactSection />
          </main>
          <div className="relative z-10">
            <Footer />
          </div>
        </div>
      </div>
      <CustomCursor />
    </div>
  );
}

export default App;
